import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Link,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  IoEye,
  IoEyeOff,
  IoCheckmark,
  IoClose,
  IoLockClosedOutline,
} from "react-icons/io5";
import config from "../config";
import { adminColors, TOUCH_HIT_SX } from "../theme/adminTheme";
import { FONT_STACK, BORDER_COLOR, BrandHeader } from "./legal/LegalPageLayout";

// Shared focus treatment for form fields — indigo ring/label, no default MUI
// asterisk. Kept identical to Signin.js / RequestAccess.js's fieldFocusSx.
const fieldFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: adminColors.accent,
    borderWidth: "1.5px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: adminColors.accent,
  },
  "& .MuiFormLabel-asterisk": {
    display: "none",
  },
};

/* ── password strength helpers ──────────────────────────────────────────
   Duplicated from InvitedRegister.js's password step rather than imported
   (neither file exports these) — keep the two rule sets in sync by hand if
   the policy ever changes. Same five checks, same colors, same copy. */
function validatePassword(password) {
  return {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
}

function PasswordCheck({ ok, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {ok ? (
        <IoCheckmark style={{ color: "#10a37f", fontSize: 14 }} />
      ) : (
        <IoClose style={{ color: "#dc2626", fontSize: 14 }} />
      )}
      <Typography variant="caption" sx={{ color: ok ? "#10a37f" : "#dc2626" }}>
        {label}
      </Typography>
    </Box>
  );
}

const STATE = {
  INVALID: "invalid", // no ?token= in the URL — never hits the API
  FORM: "form",
  SUBMITTING: "submitting",
  SUCCESS: "success",
};

// The API contract doesn't yet distinguish "token is dead" from "password
// didn't meet the policy" — both are a plain 400 with a human-readable
// `detail`. Offering "Request a new link" on every 400 is misleading for a
// password-policy rejection (it implies the link itself is broken, when the
// fix is just a different password). Until the backend adds a
// machine-readable error_code we can switch on, use a conservative text
// heuristic: only show the escape hatch when the message itself talks about
// the token/link. Anything else is assumed to be a password rejection,
// where the inline error + password rules are enough.
function looksLikeTokenError(detail) {
  return /token|link|expired|invalid.*reset/i.test(detail || "");
}

/**
 * /reset-password?token=... — there is no separate "validate this token"
 * endpoint in the contract (unlike /register's invitation flow), so token
 * validity is only known once the form is actually submitted: a bad/expired
 * token surfaces as a 400 from POST /api/password-reset/confirm, handled
 * inline below so a merely-weak-password 400 doesn't force the visitor to
 * abandon an otherwise-valid link.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [pageState, setPageState] = useState(token ? STATE.FORM : STATE.INVALID);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError, setFormError] = useState("");
  const [mounted, setMounted] = useState(false);

  const passwordRef = useRef(null);
  const headingRef = useRef(null);

  const checks = validatePassword(password);
  const pwValid = Object.values(checks).every(Boolean);
  const allValid = pwValid && password.length > 0 && password === confirm;

  useEffect(() => {
    if (pageState === STATE.FORM) {
      passwordRef.current?.focus();
    }
    setMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move focus to the outcome heading so screen reader users get the
  // "invalid link" / "success" state announced instead of landing on a page
  // that silently changed under them.
  useEffect(() => {
    if (pageState === STATE.SUCCESS || pageState === STATE.INVALID) {
      headingRef.current?.focus();
    }
  }, [pageState]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setFormError("");
    setPageState(STATE.SUBMITTING);

    try {
      const res = await fetch(`${config.API_BASE_URL}/api/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setPageState(STATE.SUCCESS);
      } else {
        // Covers both "invalid/expired token" and "weak password" per the
        // API contract — the form stays interactive either way so a weak
        // password can be fixed without leaving the page. Whether the
        // "request a new link" escape hatch also shows depends on
        // looksLikeTokenError() below, so a weak-password message doesn't
        // wrongly imply the link itself is broken.
        setFormError(data.detail || "Could not reset your password. Please try again.");
        setPageState(STATE.FORM);
      }
    } catch (err) {
      console.error("Password reset confirm failed:", err?.message ?? err);
      setFormError("Network error. Please check your connection and try again.");
      setPageState(STATE.FORM);
    }
  };

  const getStrengthPct = () => (Object.values(checks).filter(Boolean).length / 5) * 100;
  const getStrengthColor = () => {
    const s = getStrengthPct();
    if (s < 40) return "#dc2626";
    if (s < 80) return "#f59e0b";
    return "#10a37f";
  };
  const getStrengthText = () => {
    const s = getStrengthPct();
    if (s < 40) return "Weak";
    if (s < 80) return "Good";
    return "Strong";
  };

  if (!mounted) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: adminColors.surface,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: adminColors.surface,
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT_STACK,
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      <BrandHeader />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 420,
            p: 4,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "12px",
          }}
        >
          {pageState === STATE.INVALID && (
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <IoClose style={{ color: "#dc2626", fontSize: 28 }} />
              </Box>

              <Typography
                ref={headingRef}
                tabIndex={-1}
                component="h1"
                variant="h5"
                sx={{ fontWeight: 700, color: adminColors.ink, mb: 1.5, outline: "none" }}
              >
                Link invalid
              </Typography>

              <Typography variant="body2" sx={{ color: adminColors.inkSoft, lineHeight: 1.6, mb: 4 }}>
                This password reset link is missing or incomplete. It may have been
                copied incorrectly, or it may already have been used.
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/forgot-password")}
                sx={{
                  mb: 1.5,
                  py: 1.25,
                  backgroundColor: adminColors.accent,
                  "&:hover": { backgroundColor: adminColors.accentDark },
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  ...TOUCH_HIT_SX,
                }}
              >
                Request a new link
              </Button>
              <Link
                component="button"
                type="button"
                onClick={() => navigate("/signin")}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: adminColors.inkSoft,
                  cursor: "pointer",
                  "&:hover": { color: adminColors.accent },
                  ...TOUCH_HIT_SX,
                }}
              >
                Back to sign in
              </Link>
            </Box>
          )}

          {pageState === STATE.SUCCESS && (
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "#f0fdf4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <IoCheckmark style={{ color: "#10a37f", fontSize: 28 }} />
              </Box>

              <Typography
                ref={headingRef}
                tabIndex={-1}
                component="h1"
                variant="h5"
                sx={{ fontWeight: 700, color: adminColors.ink, mb: 1.5, outline: "none" }}
              >
                Password updated
              </Typography>

              <Typography variant="body2" sx={{ color: adminColors.inkSoft, lineHeight: 1.6, mb: 4 }}>
                Your password has been reset. Sign in with your new password to
                continue.
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/signin")}
                sx={{
                  py: 1.25,
                  backgroundColor: adminColors.accent,
                  "&:hover": { backgroundColor: adminColors.accentDark },
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: "8px",
                  ...TOUCH_HIT_SX,
                }}
              >
                Sign in
              </Button>
            </Box>
          )}

          {(pageState === STATE.FORM || pageState === STATE.SUBMITTING) && (
            <>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: adminColors.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <IoLockClosedOutline size={22} color="white" />
                </Box>

                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: adminColors.ink,
                    mb: 1.5,
                  }}
                >
                  Choose a new password
                </Typography>

                <Typography variant="body2" sx={{ color: adminColors.inkSoft, lineHeight: 1.6 }}>
                  Make it something you haven't used here before.
                </Typography>
              </Box>

              {formError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5, "& .MuiAlert-message": { fontSize: "14px" } }}
                >
                  {formError}
                </Alert>
              )}
              {formError && looksLikeTokenError(formError) && (
                <Box sx={{ textAlign: "right", mb: 2.5 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: adminColors.accent,
                      cursor: "pointer",
                      "&:hover": { color: adminColors.accentDark },
                      ...TOUCH_HIT_SX,
                    }}
                  >
                    Request a new link
                  </Link>
                </Box>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="New password"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  ref={passwordRef}
                  required
                  disabled={pageState === STATE.SUBMITTING}
                  helperText="8+ characters, with upper & lowercase letters, a number, and a symbol."
                  sx={{ mb: 2, ...fieldFocusSx }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPw((p) => !p)}
                          edge="end"
                          size="small"
                          aria-label={showPw ? "Hide password" : "Show password"}
                          sx={{ color: adminColors.inkSoft }}
                        >
                          {showPw ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {password && (
                  <Box sx={{ mt: -0.5, mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Password strength
                      </Typography>
                      <Typography variant="caption" sx={{ color: getStrengthColor(), fontWeight: 600 }}>
                        {getStrengthText()}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getStrengthPct()}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": { backgroundColor: getStrengthColor() },
                      }}
                    />
                    <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5 }}>
                      <PasswordCheck ok={checks.length} label="8+ characters" />
                      <PasswordCheck ok={checks.upper} label="Uppercase letter" />
                      <PasswordCheck ok={checks.lower} label="Lowercase letter" />
                      <PasswordCheck ok={checks.number} label="Number" />
                      <PasswordCheck ok={checks.special} label="Special character" />
                    </Box>
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="Confirm new password"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  disabled={pageState === STATE.SUBMITTING}
                  error={confirm.length > 0 && confirm !== password}
                  helperText={confirm.length > 0 && confirm !== password ? "Passwords do not match." : ""}
                  sx={{ mb: 3, ...fieldFocusSx }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm((p) => !p)}
                          edge="end"
                          size="small"
                          aria-label={showConfirm ? "Hide password" : "Show password"}
                          sx={{ color: adminColors.inkSoft }}
                        >
                          {showConfirm ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!allValid || pageState === STATE.SUBMITTING}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    backgroundColor: adminColors.accent,
                    "&:hover": {
                      backgroundColor: adminColors.accentDark,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: adminColors.accentBorder,
                      color: adminColors.accentDark,
                    },
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                    borderRadius: "8px",
                    transition: "transform 120ms ease-out, background-color 150ms ease",
                    "&:active": {
                      transform: pageState === STATE.SUBMITTING ? "none" : "scale(0.98)",
                    },
                  }}
                >
                  {pageState === STATE.SUBMITTING ? (
                    <CircularProgress size={20} sx={{ color: adminColors.accentDark }} />
                  ) : (
                    "Reset password"
                  )}
                </Button>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate("/signin")}
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: adminColors.inkSoft,
                    cursor: "pointer",
                    "&:hover": { color: adminColors.accent },
                    ...TOUCH_HIT_SX,
                  }}
                >
                  Back to sign in
                </Link>
              </Box>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
}
